import {useCallback} from 'react';
import {useVideoBannerContext} from '@dazn/lp-web/src/components/VideoBanner/VideoBannerContext';
import {triggerElementClick} from '../../analytics/fe-events';
import {elementClickEventTypes} from '../../analytics/fe-events/elementClick';
import {deviceDetector} from '../../utils/deviceDetector';

export const useVideoBannerPlayer = () => {
    const {
        screenRef,
        videoRef,
        isFullScreen,
        setIsPlaying,
        setPlayedProgress,
        setBufferedProgress,
        setIsMuted,
        setIsHovered,
        setIsFullScreen,
        setShowModal,
        stateResetter,
        setIsPlayedOrSkiped
    } = useVideoBannerContext();

    const video = videoRef.current;

    // Update played progress
    const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (video) {
            if (video.buffered.length > 0) {
                const bufferedEnd = video.buffered.end(
                    video.buffered.length - 1
                );
                const buggeredProgress: number =
                    (bufferedEnd / video.duration) * 100;
                setBufferedProgress(buggeredProgress);
            }
            const progress = (video.currentTime / video.duration) * 100;
            setPlayedProgress(progress);
        }
    };

    // Hover toggle function used to show/hide the controls in mobile
    const handleHover = (state: boolean) => {
        setIsHovered(state);
    };

    // Toggle play/pause on video player
    const togglePlayPause = () => {
        if (video) {
            if (video.paused) {
                video.play();
                setIsPlaying(true);
            } else {
                video.pause();
                setIsPlaying(false);
            }
            handleHover(false);
        }
    };

    // Toggle mute/unmute
    const toggleMuteUnmute = useCallback(() => {
        if (video) {
            video.muted = !video.muted;
            setIsMuted(video.muted);
        }
    }, [video]);

    // Toggle full screen on desktop
    const toggleFullScreen = useCallback(() => {
        const videoElement = screenRef.current;

        if (!videoElement) {
            return;
        }

        if (!isFullScreen) {
            videoElement
                .requestFullscreen?.()
                .then(() => setIsFullScreen(true))
                .catch((err) => new Error(err));
        } else {
            setIsFullScreen(false);
            document
                .exitFullscreen?.()
                .then(() => setIsFullScreen(false))
                .catch((err) => new Error(err));
        }
    }, [isFullScreen, screenRef, setIsFullScreen]);

    // Forward played progress 10 seconds
    const handleForward10Seconds = () => {
        if (video) {
            const newTime = Math.min(video.currentTime + 10, video.duration); // Ensure we don't go beyond video duration
            video.currentTime = newTime;
            const progress = (newTime / video.duration) * 100;
            setPlayedProgress(progress);
            handleHover(false);
        }
    };

    // Backward played progress 10 seconds
    const handleBackward10Seconds = () => {
        if (video) {
            const newTime = Math.max(video.currentTime - 10, 0); // Ensure we don't go below 0
            video.currentTime = newTime;
            const progress = (newTime / video.duration) * 100;
            setPlayedProgress(progress);
            handleHover(false);
        }
    };

    /**
     * Increases the video volume by a step, maxing out at 1.
     * @param step - The increment step (default is 0.1)
     */
    const handleVolumeUp = (step: number = 0.1): void => {
        if (!video) return;

        const newVolume = Math.min(video.volume + step, 1); // Ensure volume does not exceed 1
        video.volume = newVolume;
        if (video.muted && newVolume > 0) {
            video.muted = false;
            setIsMuted(false); // Update muted state
        }
    };

    /**
     * Decreases the video volume by a step, minning out at 0.
     * @param step - The decrement step (default is 0.1)
     */
    const handleVolumeDown = (step: number = 0.1): void => {
        if (!video) return;

        const newVolume = Math.max(video.volume - step, 0); // Ensure volume does not go below 0
        video.volume = newVolume;

        if (newVolume === 0) {
            video.muted = true; // Automatically mute if volume reaches 0
            setIsMuted(true); // Update muted state
        }
    };

    // Used in the banner to open the modal and play the video
    const handleBannerPlayButtonClick = useCallback(
        (videoUrl: string) => {
            setShowModal(true);
            setIsPlaying(true);
            if (deviceDetector.isPhoneOnlyDevice) {
                toggleFullScreen();
            }
            triggerElementClick({
                section: elementClickEventTypes.BODY,
                itemId: 'promo_video_play_cta',
                resourceString: videoUrl || ''
            });
        },
        [setIsPlaying]
    );

    // Closing the modal resets all the boolean states
    const handleCloseModal = useCallback(() => {
        stateResetter();
        setIsPlayedOrSkiped(true);
    }, [setIsPlaying]);

    return {
        togglePlayPause,
        toggleMuteUnmute,
        toggleFullScreen,
        handleHover,
        handleBannerPlayButtonClick,
        handleCloseModal,
        handleForward10Seconds,
        handleBackward10Seconds,
        handleTimeUpdate,
        handleVolumeUp,
        handleVolumeDown
    };
};
